import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Icon from "@/components/Icon";
import AdminLayout from "@/components/AdminLayout";

const CARDS = [
  { key: "companies", label: "Customers", icon: "Building2" },
  { key: "users", label: "Users", icon: "Users" },
  { key: "qrcodes", label: "QR Codes", icon: "QrCode" },
  { key: "scans", label: "Total Scans", icon: "MousePointerClick" },
  { key: "active_subscriptions", label: "Active Subs", icon: "BadgeCheck" },
  { key: "new_companies_30d", label: "New (30d)", icon: "TrendingUp" },
];

export default function AdminOverview() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/overview").then(({ data }) => setData(data)).catch(() => {}); }, []);

  return (
    <AdminLayout title="Overview" subtitle="Live platform metrics from the database">
      {!data ? (
        <div className="grid place-items-center py-20"><Icon name="Loader2" className="h-7 w-7 animate-spin text-primary" /></div>
      ) : (
        <div className="space-y-6" data-testid="admin-overview">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {CARDS.map((c) => (
              <div key={c.key} data-testid={`stat-${c.key}`} className="rounded-2xl border border-border bg-card p-5">
                <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">{c.label}</span><Icon name={c.icon} className="h-4 w-4 text-primary" /></div>
                <div className="text-3xl font-display font-extrabold mt-2">{(data.totals?.[c.key] ?? 0).toLocaleString()}</div>
              </div>
            ))}
          </div>

          {data.billing && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="overview-billing">
              <div data-testid="widget-mrr" className="rounded-2xl border border-primary/30 bg-primary/5 p-5"><div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">MRR</span><Icon name="DollarSign" className="h-4 w-4 text-primary" /></div><div className="text-3xl font-display font-extrabold mt-2">${Number(data.billing.mrr || 0).toLocaleString()}</div></div>
              <div data-testid="widget-conversion" className="rounded-2xl border border-primary/30 bg-primary/5 p-5"><div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">Trial conversion</span><Icon name="TrendingUp" className="h-4 w-4 text-primary" /></div><div className="text-3xl font-display font-extrabold mt-2">{data.billing.trial_conversion}%</div></div>
              <div className="rounded-2xl border border-border bg-card p-5"><div className="text-sm text-muted-foreground">Trialing</div><div className="text-3xl font-display font-extrabold mt-2">{data.billing.trials}</div></div>
              <div className="rounded-2xl border border-border bg-card p-5"><div className="text-sm text-muted-foreground">Canceled</div><div className="text-3xl font-display font-extrabold mt-2">{data.billing.canceled}</div></div>
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-5">
            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="text-sm font-bold mb-3">Plan distribution</h3>
              <div className="space-y-2">
                {Object.keys(data.plan_distribution || {}).length === 0 && <p className="text-sm text-muted-foreground">No customers yet.</p>}
                {Object.entries(data.plan_distribution || {}).map(([plan, count]) => (
                  <div key={plan} className="flex items-center justify-between text-sm">
                    <span className="capitalize font-medium">{plan}</span>
                    <span className="font-mono text-muted-foreground">{count}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5">
              <div className="flex items-center justify-between mb-3"><h3 className="text-sm font-bold">Recent customers</h3><Link to="/admin/customers" className="text-xs font-semibold text-primary">View all</Link></div>
              <div className="space-y-2">
                {(data.recent_companies || []).length === 0 && <p className="text-sm text-muted-foreground">No customers yet.</p>}
                {(data.recent_companies || []).map((c) => (
                  <Link key={c.id} to={`/admin/customers/${c.id}`} className="flex items-center justify-between text-sm rounded-lg px-2 py-1.5 hover:bg-accent">
                    <span className="font-medium truncate">{c.name}</span>
                    <span className="capitalize text-xs text-muted-foreground">{c.plan || "basic"}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
